/**
 * Authentication error handling utilities
 * Provides user-friendly error messages and retry mechanisms for AWS Amplify Auth errors
 */

export interface AuthError {
  type: "network" | "credentials" | "session" | "unknown";
  message: string;
  retryable: boolean;
  originalError?: any;
}

/**
 * Parse AWS Amplify Auth errors into user-friendly messages
 */
export const parseAuthError = (error: any): AuthError => {
  // Handle network errors (no internet, server down, etc.)
  if (!navigator.onLine) {
    return {
      type: "network",
      message:
        "No internet connection. Please check your network and try again.",
      retryable: true,
      originalError: error,
    };
  }

  // Handle AWS Amplify specific errors
  if (error?.name || error?.code) {
    const errorCode = error.name || error.code;

    switch (errorCode) {
      case "NotAuthorizedException":
      case "UserNotFoundException":
      case "IncorrectUsernameOrPasswordException":
        return {
          type: "credentials",
          message:
            "Invalid username or password. Please check your credentials and try again.",
          retryable: false,
          originalError: error,
        };

      case "NetworkError":
        return {
          type: "network",
          message:
            "Network error occurred. Please check your connection and try again.",
          retryable: true,
          originalError: error,
        };

      case "ServiceException":
      case "InternalErrorException":
        return {
          type: "network",
          message:
            "Service temporarily unavailable. Please try again in a few moments.",
          retryable: true,
          originalError: error,
        };

      case "TokenRefreshException":
      case "NotSignedInException":
      case "SignedOutException":
        return {
          type: "session",
          message: "Your session has expired. Please sign in again.",
          retryable: false,
          originalError: error,
        };

      default:
        // Check if it's a network-related error by message content
        const errorMessage = error.message?.toLowerCase() || "";
        if (
          errorMessage.includes("network") ||
          errorMessage.includes("fetch") ||
          errorMessage.includes("timeout") ||
          errorMessage.includes("connection")
        ) {
          return {
            type: "network",
            message:
              "Connection error. Please check your internet connection and try again.",
            retryable: true,
            originalError: error,
          };
        }
    }
  }

  // Handle generic Error objects
  if (error instanceof Error) {
    const errorMessage = error.message.toLowerCase();

    // Check for network-related errors in the message
    if (
      errorMessage.includes("network") ||
      errorMessage.includes("fetch") ||
      errorMessage.includes("timeout") ||
      errorMessage.includes("connection") ||
      errorMessage.includes("cors")
    ) {
      return {
        type: "network",
        message:
          "Connection error. Please check your internet connection and try again.",
        retryable: true,
        originalError: error,
      };
    }

    // Check for credential-related errors
    if (
      errorMessage.includes("unauthorized") ||
      errorMessage.includes("invalid") ||
      errorMessage.includes("incorrect")
    ) {
      return {
        type: "credentials",
        message:
          "Invalid username or password. Please check your credentials and try again.",
        retryable: false,
        originalError: error,
      };
    }
  }

  // Fallback for unknown errors
  return {
    type: "unknown",
    message:
      "An unexpected error occurred. Please try again or contact support if the problem persists.",
    retryable: true,
    originalError: error,
  };
};

/**
 * Retry configuration for different error types
 */
export const RETRY_CONFIG = {
  network: {
    maxAttempts: 3,
    baseDelay: 1000, // 1 second
    maxDelay: 10000, // 10 seconds
    backoffMultiplier: 2,
  },
  session: {
    maxAttempts: 1,
    baseDelay: 0,
    maxDelay: 0,
    backoffMultiplier: 1,
  },
  credentials: {
    maxAttempts: 1,
    baseDelay: 0,
    maxDelay: 0,
    backoffMultiplier: 1,
  },
  unknown: {
    maxAttempts: 2,
    baseDelay: 2000, // 2 seconds
    maxDelay: 5000, // 5 seconds
    backoffMultiplier: 1.5,
  },
};

/**
 * Calculate delay for retry attempts with exponential backoff
 */
export const calculateRetryDelay = (
  errorType: AuthError["type"],
  attemptNumber: number,
): number => {
  const config = RETRY_CONFIG[errorType];
  const delay = Math.min(
    config.baseDelay * Math.pow(config.backoffMultiplier, attemptNumber - 1),
    config.maxDelay,
  );

  // Add some jitter to prevent thundering herd
  const jitter = Math.random() * 0.1 * delay;
  return Math.floor(delay + jitter);
};

/**
 * Check if an error type should be retried
 */
export const shouldRetry = (
  errorType: AuthError["type"],
  attemptNumber: number,
): boolean => {
  const config = RETRY_CONFIG[errorType];
  return attemptNumber < config.maxAttempts;
};
